import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import AutoLogoutWarningModal from '../components/Auth/AutoLogoutWarningModal';

interface AutoLogoutContextType {
  resetInactivityTimer: () => void;
  isWarningModalOpen: boolean;
  timeRemaining: number;
}

const AutoLogoutContext = createContext<AutoLogoutContextType | undefined>(undefined);

interface AutoLogoutProviderProps {
  children: ReactNode;
}

export const AutoLogoutProvider: React.FC<AutoLogoutProviderProps> = ({ children }) => {
  const { logout, isAuthenticated } = useAuth();
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const isLoggingOutRef = useRef<boolean>(false);
  
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Configuration
  const INACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 minutes in milliseconds
  const WARNING_TIME = 2 * 60 * 1000; // 2 minutes warning before logout
  const COUNTDOWN_INTERVAL = 1000; // 1 second countdown updates

  // Clear all timers
  const clearAllTimers = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  // Reset the inactivity timer
  const resetInactivityTimer = () => {
    // Don't reset if not authenticated or already logging out
    if (!isAuthenticated || isLoggingOutRef.current) return;

    lastActivityRef.current = Date.now();
    clearAllTimers();
    setIsWarningModalOpen(false);
    setTimeRemaining(0);

    // Set warning timer (18 minutes)
    warningTimerRef.current = setTimeout(() => {
      setIsWarningModalOpen(true);
      setTimeRemaining(WARNING_TIME);
      
      // Start countdown
      countdownTimerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= COUNTDOWN_INTERVAL) {
            // Time's up - logout
            clearAllTimers();
            isLoggingOutRef.current = true;
            logout();
            return 0;
          }
          return prev - COUNTDOWN_INTERVAL;
        });
      }, COUNTDOWN_INTERVAL);
    }, INACTIVITY_TIMEOUT - WARNING_TIME);

    // Set final logout timer (20 minutes)
    inactivityTimerRef.current = setTimeout(() => {
      clearAllTimers();
      isLoggingOutRef.current = true;
      logout();
    }, INACTIVITY_TIMEOUT);
  };

  // Handle user activity
  const handleActivity = () => {
    resetInactivityTimer();
  };

  // Handle warning modal actions
  const handleStayLoggedIn = () => {
    resetInactivityTimer();
  };

  const handleLogoutNow = () => {
    clearAllTimers();
    isLoggingOutRef.current = true;
    logout();
  };

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setIsWarningModalOpen(false);
      setTimeRemaining(0);
      isLoggingOutRef.current = false;
      return;
    }

    // Reset logout flag when user becomes authenticated
    isLoggingOutRef.current = false;

    // Start the timer when user is authenticated
    resetInactivityTimer();

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup function
    return () => {
      clearAllTimers();
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isAuthenticated]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  const value: AutoLogoutContextType = {
    resetInactivityTimer,
    isWarningModalOpen,
    timeRemaining
  };

  return (
    <AutoLogoutContext.Provider value={value}>
      {children}
      <AutoLogoutWarningModal
        isOpen={isWarningModalOpen}
        timeRemaining={timeRemaining}
        onStayLoggedIn={handleStayLoggedIn}
        onLogoutNow={handleLogoutNow}
      />
    </AutoLogoutContext.Provider>
  );
};

export const useAutoLogout = () => {
  const context = useContext(AutoLogoutContext);
  if (context === undefined) {
    throw new Error('useAutoLogout must be used within an AutoLogoutProvider');
  }
  return context;
};

